<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractValidityData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_hi1g4absq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3wfodfuod =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_hi1g4absq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3wfodfuod],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_3wfodfuod,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_3wfodfuod = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3wfodfuod.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend>
      {{ pgettext('Contract', 'Validity') }}
    </legend>
    <ItpFormGroup
      name="contract_date"
      :label="pgettext('Contract', 'Contract date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime name="contract_date" v-model="contract.contract_date">
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="valid_from"
      :label="pgettext('Contract', 'Valid from')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime name="valid_from" v-model="contract.valid_from">
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="valid_to"
      :label="pgettext('Contract', 'Valid to')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime name="valid_to" v-model="contract.valid_to">
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="valid_to"
      :label="pgettext('Contract', 'Auto renewal')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox name="auto_renewal" v-model="contract.auto_renewal">
      </ItpFormCheckbox>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
